import { applyTax, CompanyParametersQuery, ContextKeys, CurrencyCollectionQuery, propertyChangedJoinPointFilter, removeTax, VatRateForPurchaseQuery, VatRateForSaleQuery, VatRateItem, withModule } from '@skylab-shared/business';
import { BO, BusinessObjectBase, NonUniqueModelDataError, PropertyChangedJoinPoint, PropertyChangedJoinPointContext, QueryBus, RepositoryResponseConflictCodes, ResponseConflictObject, Weaver } from '@skylab-shared/core';
import { ProductKind, ProductModel, ProductOssVatRateModel, ProductPriceModel } from '@skylab-shared/dataModel';
import { Calc, MidpointRounding, not, withFlag } from '@skylab-shared/utils';
import { ProductGeneratingSymbolQuery } from './queries/product-generating-symbol.query';
import { ProductsWithSamePropertyQuery, ProductWithSameNameQueryProperties, ProductWithSameSymbolQueryProperties } from './queries/products-with-same-property.query';
import { GrossPriceLessThanZeroError } from './violations/gross-price-less-than-zero-error';
import { InventoryControlOffOnNonEmptyInventoryLevel } from './violations/inventory-control-off-on-non-empty-inventory-level-warning';
import { InventoryLevelWarningLessThanZeroError } from './violations/inventory-level-warning-less-than-zero-error';
import { NetPriceLessThanZeroError } from './violations/net-price-less-than-zero-error';
import { NonUniqueNameWarning } from './violations/non-unique-name-warning';
import { NonUniqueSymbolError } from './violations/non-unique-symbol-error';
import { OssVatRateCannotBeEmptyError } from './violations/oss-vat-rate-cannot-be-empty-error';

@BO({ modelType: ProductModel })
export class ProductBO extends BusinessObjectBase<ProductModel> {
    private canGenerateSymbol: boolean | null = null;
    private autoGenerated: boolean | null = null;

    public constructor(
        weaver: Weaver,
        private readonly queryBus: QueryBus
    ) {
        super(weaver);
    }

    protected override async initializeAsync(): Promise<void> {
        await super.initializeAsync();

        await Promise.resolve([
            this.localContext.preload(CompanyParametersQuery),
            this.localContext.preload(VatRateForSaleQuery),
            this.localContext.preload(VatRateForPurchaseQuery),
            this.localContext.preload(CurrencyCollectionQuery)
        ]);

        this.configureValidationRules();
        this.configureReadonlyRules();
        this.registerCoreFeatures();

        this.canGenerateSymbol = !this.model.isModified;
    }

    protected override handleConflictSaveResponse(conflict: ResponseConflictObject<RepositoryResponseConflictCodes>) {
        if (conflict.code !== RepositoryResponseConflictCodes.UniqueConstraintViolation) return;

        if (conflict.objectProperties?.includes('symbol')) {
            this.addExternalRuleViolations({
                [this.model.sun]: [{
                    property: 'symbol',
                    violation: new NonUniqueModelDataError('business.product.validation.server-rule-violations.symbol-non-unique-model-data-error')
                }]
            });
        }
    }

    private configureValidationRules(): void {
        if (withModule('Inventory')) {
            this.validation.addRule(
                [[ProductModel, 'inventoryControl']],
                ctx => not(!ctx.bo.model.inventoryControl && (ctx.bo.model.inventoryLevel > 0)),
                InventoryControlOffOnNonEmptyInventoryLevel
            );

            this.validation.addRule(
                [[ProductModel, 'inventoryWarningLevel']],
                ctx => !(ctx.source.inventoryWarningLevel < 0),
                InventoryLevelWarningLessThanZeroError
            );
        }

        this.validation.addRule(
            [[ProductPriceModel, 'net']],
            ctx => {
                return !(ctx.source.net < 0);
            },
            NetPriceLessThanZeroError
        );

        this.validation.addRule(
            [[ProductPriceModel, 'gross']],
            ctx => {
                return !(ctx.source.gross < 0);
            },
            GrossPriceLessThanZeroError
        );

        this.validation.addRule(
            [[ProductOssVatRateModel, 'vatRateId']],
            ctx => {
                return !!ctx.source.vatRateId;
            },
            OssVatRateCannotBeEmptyError
        );

        this.validation.addRule(
            [[ProductModel, 'name']],
            async ctx => {
                if (!ctx.source.name) return true;
                if (this.model.isPersistent && !this.model.active) return true;

                const uniqueNameQueryParam: ProductWithSameNameQueryProperties = {
                    exactName: ctx.source.name,
                    omitedClientId: ctx.source.id
                };

                try {
                    const query = new ProductsWithSamePropertyQuery(uniqueNameQueryParam);
                    return !(await this.localContext.getAsync(ContextKeys.productName(ctx.source.name), () => this.queryBus.queryAsync(query)));
                } catch (err) {
                    return true;
                }
            },
            NonUniqueNameWarning
        );

        this.validation.addRule(
            [[ProductModel, 'symbol']],
            async ctx => {
                if (!ctx.source.symbol) return true;
                if (this.model.isPersistent && !this.model.active) return true;

                const uniqueSymbolQueryParam: ProductWithSameSymbolQueryProperties = {
                    symbol: ctx.source.symbol,
                    omitedClientId: ctx.source.id
                };

                try {
                    const query = new ProductsWithSamePropertyQuery(uniqueSymbolQueryParam);
                    return !(await this.localContext.getAsync(ContextKeys.productSymbol(ctx.source.symbol), () => this.queryBus.queryAsync(query)));
                } catch (err) {
                    return true;
                }
            },
            NonUniqueSymbolError
        );
    }

    private configureReadonlyRules(): void {
        this.readonly.setAsReadonly([ProductModel, 'inventoryLevel']);

        this.readonly.addRule([
            [ProductModel, 'unitId'],
            [ProductModel, 'inventoryControl']
        ], ctx => ctx.bo.model.isPersistent && !ctx.bo.model.modifiedProperties.has('inventoryControl') && ctx.bo.model.inventoryControl);

        this.readonly.addRule(
            [[ProductModel, 'inventoryWarningLevel'], [ProductModel, 'inventoryControl']],
            [[ProductModel, 'inventoryWarningLevel']],
            ctx => !ctx.source.inventoryControl);
    }

    //#region Core feature actions

    private registerCoreFeatures(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductPriceModel, ['net', 'gross'])(
            ctx => withFlag(() => {
                if (this.model.purchasePrice === ctx.model) {
                    const vatRate = this.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: this.model.purchaseVatRateId });
                    return this.recalculatePrices(ctx, vatRate);
                } else {
                    const vatRate = this.localContext.firstPreloaded(VatRateForSaleQuery, { id: this.model.vatRateId });
                    return this.recalculatePrices(ctx, vatRate);
                }
            }, `${this.model.sun}#price-change`)
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductModel, ['vatRateId'])(
            () => withFlag(() => this.recalculatePricesOnVatRateChange(), `${this.model.sun}#price-change`)
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductModel, ['purchaseVatRateId'])(
            () => withFlag(() => this.recalculatePurchasePriceOnVatRateChange(), `${this.model.sun}#price-change`)
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductModel, ['kind'])(
            () => {
                if (this.model.kind === ProductKind.Service) {
                    if (this.model.inventoryControl) {
                        this.model.inventoryWarningLevel = this.model.inventoryLevel = 0;
                        this.model.inventoryControl = false;
                    }
                }
            }
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductModel, ['name'])(
            async () => {
                if (this.canGenerateSymbol) {
                    this.autoGenerated = true;
                    this.model.symbol = await this.generateSymbol();
                }
            }
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this)(ProductModel, ['symbol'])(
            () => {
                if (!this.autoGenerated) {
                    this.canGenerateSymbol = false;
                }
                this.autoGenerated = false;
            }
        )));
    }

    private async generateSymbol(): Promise<string> {
        const name = this.model?.name?.trim();
        if (!name) return "";
        try {
            const query = new ProductGeneratingSymbolQuery(name);
            return await this.queryBus.queryAsync(query);
        } catch (err) {
            this.logger.error(err);
            return "";
        }
    }

    private recalculatePrices(ctx: PropertyChangedJoinPointContext, vatRate: VatRateItem): void {
        const model = ctx.model as ProductPriceModel;

        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: model.currencyId });

        if (ctx.propertyName === 'gross') {
            const net = removeTax(model.gross, vatRate.rate);
            model.net = Calc.round(net, precision, MidpointRounding.AwayFromZero);
        } else if (ctx.propertyName === 'net') {
            const gross = applyTax(model.net, vatRate.rate);
            model.gross = Calc.round(gross, precision, MidpointRounding.AwayFromZero);
        }
    }

    private recalculatePricesOnVatRateChange(): void {
        const { rate } = this.localContext.firstPreloaded(VatRateForSaleQuery, { id: this.model.vatRateId });

        this.model.prices.forEach(elm => {
            const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: elm.currencyId });

            const gross = applyTax(elm.net, rate);
            elm.gross = Calc.round(gross, precision, MidpointRounding.AwayFromZero);
        });
    }

    private recalculatePurchasePriceOnVatRateChange(): void {
        const price = this.model.purchasePrice;
        const { rate } = this.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: this.model.purchaseVatRateId });
        const { precision } = this.localContext.firstPreloaded(CurrencyCollectionQuery, { id: price.currencyId });

        const gross = applyTax(price.net, rate);
        price.gross = Calc.round(gross, precision, MidpointRounding.AwayFromZero);
    }

    //#endregion
}
